<template>
    <AccordionItem
        v-if="collapsible"
        :as="itemAs"
        :value="stepValue"
        :disabled="disabled"
        :class="classes"
    >
        <span
            :class="markerClasses"
            aria-hidden="true"
        >
            <slot name="marker" />
        </span>

        <AccordionHeader
            :as="titleAs"
            :class="element('header')"
        >
            <AccordionTrigger :class="element('trigger')">
                <span :class="element('title')">
                    <slot name="title">{{ title }}</slot>
                </span>

                <span
                    :class="element('chevron')"
                    aria-hidden="true"
                >
                    <slot name="chevron">
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path d="m6 9 6 6 6-6" />
                        </svg>
                    </slot>
                </span>
            </AccordionTrigger>
        </AccordionHeader>

        <AccordionContent :class="element('content')">
            <div :class="element('body')">
                <slot />
            </div>
        </AccordionContent>
    </AccordionItem>

    <Primitive
        v-else
        :as="itemAs"
        :class="classes"
    >
        <span
            :class="markerClasses"
            aria-hidden="true"
        >
            <slot name="marker" />
        </span>

        <component
            :is="titleAs"
            :class="element('header')"
        >
            <span :class="element('title')">
                <slot name="title">{{ title }}</slot>
            </span>
        </component>

        <div
            v-if="$slots.default"
            :class="element('content')"
        >
            <div :class="element('body')">
                <slot />
            </div>
        </div>
    </Primitive>
</template>

<script setup lang="ts">
import type { KStepProps } from './types'
import useBem from '@yeeroen/use-bem'
import { AccordionContent, AccordionHeader, AccordionItem, AccordionTrigger, Primitive } from 'reka-ui'
import { computed, inject, useId, useSlots } from 'vue'
import { K_STEPS_CONTEXT } from './context'

defineOptions({ name: 'KStep' })

const props = defineProps<KStepProps>()

defineSlots<{
    /** The step's content. Collapses behind the title in a `collapsible` list. */
    default?: () => unknown
    /** Title with markup, replacing the `title` prop. */
    title?: () => unknown
    /** Replaces the ordinal in the marker — a check glyph for a completed step, say. */
    marker?: () => unknown
    /** Replaces the disclosure chevron. Ignored in a static list. */
    chevron?: () => unknown
}>()

const slots = useSlots()
const steps = inject(K_STEPS_CONTEXT, null)
const { element, modifier } = useBem('k-steps')

// Rendering as a static list item outside a KSteps is the sane fallback: a lone step still
// reads as one, it just has no list to be numbered in.
const collapsible = computed(() => steps?.collapsible.value ?? false)
const titleAs = computed(() => steps?.titleAs.value ?? 'h3')
const itemAs = computed(() => steps?.itemAs.value ?? 'li')

// reka keys the open-state model by value, so a step without one cannot be addressed by
// `defaultValue` or `v-model` — but it should still open when clicked.
const fallbackValue = useId() ?? 'k-step'
const stepValue = computed(() => props.value ?? fallbackValue)

if (import.meta.env.DEV && collapsible.value && props.value === undefined) {
    console.warn(
        '[KStep] is inside a `collapsible` KSteps but has no `value`. A generated id is used '
        + 'instead, which no `default-value` or `v-model` can name.',
    )
}

const classes = computed(() => element('step', modifier('disabled', props.disabled)))

// The ordinal is CSS-generated, so it has to be suppressed rather than replaced when the
// caller supplies its own marker.
const markerClasses = computed(() => element('marker', modifier('ordinal', !slots.marker)))
</script>
